import { useState, useEffect } from "react";
import styles from "./nearMeOption.module.scss";
import PropertiesNearby from "@Icons/PropertiesNearby";
import LoaderIcon from "@Icons/LoaderIcon";
import formatClassNames from "@Utils/clientUtils/stringUtility/formatClassNames";
import useTranslationHook from "hooks/useTranslationHook";
import wrapperHOC from "@Utils/wrapperHOC";
import { buildNearbyUrl, findNearestRegion, setNearbyCookies, slugify } from "@Utils/nearbySearch";
import gtagEventEmitter from "helpers/gtagEventEmitter";

interface NearMeOptionProps {
  onNavigate: (url: string) => void;
  className?: string;
}

const GEOLOCATION_TIMEOUT = 10000;

const NearMeOption = ({ onNavigate, className }: NearMeOptionProps) => {
  const { t } = useTranslationHook();
  const [isLoading, setIsLoading] = useState(false);
  const [isSupported, setIsSupported] = useState(false);

  useEffect(() => {
    setIsSupported(typeof navigator !== "undefined" && !!navigator.geolocation);
  }, []);

  if (!isSupported) {
    return null;
  }

  const handleClick = async () => {
    if (isLoading) return;
    setIsLoading(true);

    // Fire gtag event when "Properties Near Me" is clicked
    gtagEventEmitter({
      action: "properties_near_me_clicked",
      category: "nearby_properties",
      label: "button_clicked",
    });

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude: lat, longitude: lng } = position.coords;

        // Always set the cookies first so they're available for the API call
        setNearbyCookies(lat, lng);

        const regionData = await findNearestRegion(lat, lng);
        setIsLoading(false);

        if (regionData) {
          // If we found a region in our DB, navigate to it with nearby params
          // Use region name to bust cache: locality=near-by-{region_name}
          gtagEventEmitter({
            action: "nearby_page_navigated",
            category: "nearby_properties",
            label: `region:${regionData.canonical_name}`,
            isNearby: true,
          });

          onNavigate(buildNearbyUrl(regionData.canonical_name, slugify(regionData.name)));
        } else {
          // If no region found in our DB, navigate to the fallback nearby page
          onNavigate(`/nearby`);
        }
      },
      (error) => {
        console.error("Geolocation error:", error);
        setIsLoading(false);
      },
      { timeout: GEOLOCATION_TIMEOUT, enableHighAccuracy: false },
    );
  };

  return (
    <div
      className={formatClassNames(styles.container, className)}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleClick();
        }
      }}
      data-testid="near-me-option"
    >
      <span className={styles.icon}>
        {isLoading ? (
          <span className={styles.loader} data-testid="near-me-loader">
            <LoaderIcon size={16} />
          </span>
        ) : (
          <PropertiesNearby />
        )}
      </span>
      <span className={styles.label}>{t("nearme.option.label")}</span>
    </div>
  );
};

export default wrapperHOC(NearMeOption, {
  componentName: "NearMeOption-HomePage",
  showForChina: true,
});
